import { Injectable } from '@angular/core';
import ExcelJS from 'exceljs';

export type ExcelScalarType =
  | 'string'
  | 'number'
  | 'int'
  | 'boolean'
  | 'date'
  | 'isoDate'
  | 'any';

export interface ExcelColumnSpec<TOut = any> {
  /** Column header text you expect in the spreadsheet */
  header: string;

  /** Optional alternative header names */
  aliases?: string[];

  /** Field name in the output object */
  field: string;

  /** How to coerce the cell value */
  type: ExcelScalarType;

  /** If true, used to find the header row and enforce presence */
  required?: boolean;

  /** Default if the cell is blank/invalid */
  defaultValue?: any;

  /**
   * Optional transform after coercion (good for mapping codes like USNYC -> NY)
   * Receives coerced value and the entire raw row context.
   */
  transform?: (value: any, ctx: { raw: Record<string, any>; rowNumber: number }) => TOut;

  /**
   * Optional validator; if returns false, an error is recorded.
   * (Row still returned unless you filter it out yourself.)
   */
  validate?: (value: any, ctx: { raw: Record<string, any>; rowNumber: number }) => boolean;
}

export interface ExcelReadOptions {
  sheetIndex?: number;             // default 0
  headerRow?: 'auto' | number;     // default 'auto'
  startRow?: number;               // if you know data start
  stopOnBlankRow?: boolean;        // default false
  date1904Override?: boolean;      // rarely needed
  /**
   * If true, will skip rows that end up with all empty output fields.
   * default true
   */
  skipEmptyRows?: boolean;
}

export interface ExcelReadResult<T> {
  rows: T[];
  errors: Array<{ rowNumber: number; field?: string; message: string }>;
}

@Injectable({ providedIn: 'root' })
export class ExcelReaderService {

  // -----------------------
  // Public API
  // -----------------------

  async readFromFile<T = any>(
    file: File,
    schema: ExcelColumnSpec[],
    options?: ExcelReadOptions
  ): Promise<ExcelReadResult<T>> {
    const ab = await file.arrayBuffer();
    return this.readFromArrayBuffer<T>(ab, schema, options);
  }

  async readFromArrayBuffer<T = any>(
    ab: ArrayBuffer,
    schema: ExcelColumnSpec[],
    options?: ExcelReadOptions
  ): Promise<ExcelReadResult<T>> {
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(ab);

    const ws = wb.worksheets[options?.sheetIndex ?? 0];
    if (!ws) return { rows: [], errors: [{ rowNumber: 0, message: 'Worksheet not found.' }] };

    const date1904 = options?.date1904Override ?? !!wb.properties?.date1904;

    // 1) determine header row and build header->col index
    const headerRowIdx =
      options?.headerRow === 'auto' || options?.headerRow == null
        ? this.findHeaderRow(ws, schema)
        : options.headerRow;

    if (!headerRowIdx || headerRowIdx < 1) {
      return {
        rows: [],
        errors: [{ rowNumber: 0, message: 'Header row not found (no row matched required headers).' }]
      };
    }

    const headerIndex = this.buildHeaderIndex(ws.getRow(headerRowIdx));

    // 2) map schema fields -> column numbers (resolve aliases)
    const map = this.resolveSchemaToColumns(schema, headerIndex);

    // 3) validate required columns exist
    const errors: ExcelReadResult<T>['errors'] = [];
    for (const colSpec of schema) {
      if (colSpec.required) {
        const colNum = map.get(colSpec.field);
        if (!colNum || colNum < 1) {
          errors.push({
            rowNumber: headerRowIdx,
            field: colSpec.field,
            message: `Missing required column: "${colSpec.header}"`
          });
        }
      }
    }
    if (errors.some(e => e.message.startsWith('Missing required column'))) {
      // return early because parsing would be meaningless
      return { rows: [], errors };
    }

    // 4) read data rows
    const rows: T[] = [];
    const startRow = options?.startRow ?? (headerRowIdx + 1);
    const skipEmptyRows = options?.skipEmptyRows ?? true;

    for (let r = startRow; r <= ws.rowCount; r++) {
      const row = ws.getRow(r);
      if (!row || row.cellCount === 0) {
        if (options?.stopOnBlankRow) break;
        continue;
      }

      // get raw values by header field (uncoerced)
      const raw: Record<string, any> = {};
      for (const spec of schema) {
        const colNum = map.get(spec.field) ?? -1;
        raw[spec.field] = colNum > 0 ? this.getCellValue(row.getCell(colNum)) : '';
      }

      // coerce & build output object
      const out: any = {};
      let anyNonEmpty = false;

      for (const spec of schema) {
        const ctx = { raw, rowNumber: r };
        const rawVal = raw[spec.field];

        let value = this.coerce(rawVal, spec.type, date1904);
        if (this.isBlank(value)) value = spec.defaultValue ?? this.defaultFor(spec.type);

        if (spec.transform) value = spec.transform(value, ctx);

        if (spec.validate && !spec.validate(value, ctx)) {
          errors.push({ rowNumber: r, field: spec.field, message: `Validation failed for "${spec.field}".` });
        }

        out[spec.field] = value;

        if (!this.isBlank(value) && value !== this.defaultFor(spec.type)) {
          anyNonEmpty = true;
        }
      }

      // skip rows that are effectively empty
      if (skipEmptyRows && !anyNonEmpty) {
        if (options?.stopOnBlankRow) break;
        continue;
      }

      rows.push(out as T);
    }

    return { rows, errors };
  }

  // -----------------------
  // Header detection
  // -----------------------

  private findHeaderRow(ws: ExcelJS.Worksheet, schema: ExcelColumnSpec[]): number {
    const required = schema.filter(s => s.required);
    const requiredNames = required.length ? required : schema; // if none marked required, use all

    let found = -1;

    ws.eachRow({ includeEmpty: false }, (row, idx) => {
      if (found > 0) return;

      const headerIndex = this.buildHeaderIndex(row);

      // A row is header if it contains ALL required headers (or their aliases)
      const ok = requiredNames.every(spec => {
        const candidates = [spec.header, ...(spec.aliases ?? [])].map(x => this.norm(x));
        return candidates.some(c => headerIndex.has(c));
      });

      if (ok) found = idx;
    });

    return found;
  }

  private buildHeaderIndex(row: ExcelJS.Row): Map<string, number> {
    const map = new Map<string, number>();

    // ExcelJS Row.values is 1-based; index 0 unused
    const values = Array.isArray(row.values) ? row.values : [];
    for (let c = 1; c < values.length; c++) {
      const text = this.cellToVisibleString(values[c]).trim();
      if (!text) continue;
      map.set(this.norm(text), c);
    }

    return map;
  }

  private resolveSchemaToColumns(
    schema: ExcelColumnSpec[],
    headerIndex: Map<string, number>
  ): Map<string, number> {
    const out = new Map<string, number>();

    for (const spec of schema) {
      const candidates = [spec.header, ...(spec.aliases ?? [])].map(x => this.norm(x));
      const hit = candidates.map(c => headerIndex.get(c)).find(n => !!n && n > 0);
      out.set(spec.field, hit ?? -1);
    }

    return out;
  }

  // -----------------------
  // Coercion helpers
  // -----------------------

  private coerce(v: any, type: ExcelScalarType, date1904: boolean): any {
    switch (type) {
      case 'string':
        return this.isBlank(v) ? '' : String(v).trim();

      case 'number': {
        const n = this.toNumber(v);
        return Number.isFinite(n) ? n : NaN;
      }

      case 'int': {
        const n = this.toInt(v);
        return Number.isFinite(n) ? n : 0;
      }

      case 'boolean':
        return this.toBoolean(v);

      case 'date':
        return this.coerceExcelDateLike(v, date1904);

      case 'isoDate': {
        const d = this.coerceExcelDateLike(v, date1904);
        return d ? this.toISO(d) : null;
      }

      case 'any':
      default:
        return v;
    }
  }

  private defaultFor(type: ExcelScalarType): any {
    switch (type) {
      case 'string': return '';
      case 'number': return NaN;
      case 'int': return 0;
      case 'boolean': return false;
      case 'date': return null;
      case 'isoDate': return null;
      default: return null;
    }
  }

  private toNumber(v: any): number {
    if (typeof v === 'number') return v;
    const s = String(v ?? '').replace(/[, ]+/g, '').trim();
    const n = Number(s);
    return Number.isFinite(n) ? n : NaN;
  }

  private toInt(v: any): number {
    const s = String(v ?? '').replace(/[^\d\-]+/g, '').trim();
    const n = parseInt(s, 10);
    return Number.isFinite(n) ? n : 0;
  }

  private toBoolean(v: any): boolean {
    if (typeof v === 'boolean') return v;
    const s = String(v ?? '').trim().toLowerCase();
    if (['true', 'yes', 'y', '1', 'checked', '☑'].includes(s)) return true;
    if (['false', 'no', 'n', '0', 'unchecked', '☐'].includes(s)) return false;
    // Excel sometimes uses 1/0 numerics
    const n = this.toNumber(v);
    if (Number.isFinite(n)) return n !== 0;
    return false;
  }

  /** Excel serial date -> JS Date (supports 1900/1904). */
  private fromExcelSerial(serial: number, date1904: boolean): Date {
    const excelEpoch = date1904
      ? new Date(Date.UTC(1904, 0, 1))
      : new Date(Date.UTC(1899, 11, 30));

    let days = serial;
    if (!date1904) {
      // Adjust for Excel 1900 leap-year bug
      if (serial >= 60) days -= 1;
    }

    return new Date(excelEpoch.getTime() + days * 24 * 60 * 60 * 1000);
  }

  /** Coerce serial/Date/string to Date or null. */
  private coerceExcelDateLike(v: any, date1904: boolean): Date | null {
    if (v == null || v === '') return null;
    if (v instanceof Date && !isNaN(v.getTime())) return v;

    const asNum =
      (typeof v === 'number' && isFinite(v)) ? v :
        (typeof v === 'string' && /^[+-]?\d+(\.\d+)?$/.test(v.trim())) ? Number(v.trim()) :
          NaN;

    if (isFinite(asNum) && asNum > 1 && asNum < 100000) {
      return this.fromExcelSerial(asNum, date1904);
    }

    const d = new Date(v);
    return isNaN(d.getTime()) ? null : d;
  }

  private toISO(d: Date): string {
    return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
      .toISOString()
      .substring(0, 10);
  }

  // -----------------------
  // Cell reading helpers
  // -----------------------

  private getCellValue(cell: ExcelJS.Cell): any {
    const v: any = cell.value;

    if (v == null) return '';

    // ✅ FIX: ExcelJS frequently returns actual JS Date objects for date cells
    if (v instanceof Date) return v;

    if (typeof v === 'string' || typeof v === 'number' || typeof v === 'boolean') return v;

    if (typeof v === 'object') {
      if ('result' in v && v.result != null) return v.result;
      if ('richText' in v && Array.isArray(v.richText)) {
        return v.richText.map((rt: any) => rt?.text ?? '').join('');
      }

      // If formula but result exists, return result; otherwise blank
      if ('formula' in v) {
        return ('result' in v && v.result != null) ? v.result : '';
      }
    }

    return '';
  }

  private cellToVisibleString(v: any): string {
    if (v == null) return '';
    if (typeof v === 'string') return v;
    if (typeof v === 'number' || typeof v === 'boolean') return String(v);

    const val = (v && typeof v === 'object' && 'value' in v) ? (v as any).value : v;
    if (val == null) return '';

    if (typeof val === 'object') {
      if ('richText' in val && Array.isArray(val.richText)) {
        return val.richText.map((rt: any) => rt?.text ?? '').join('');
      }
      if ('result' in val && val.result != null) return String(val.result);
      if ('formula' in val) return '';
      if (val instanceof Date) return this.toISO(val);
    }

    return String(val);
  }

  private norm(s: any): string {
    return String(s ?? '').replace(/\s+/g, ' ').trim().toLowerCase();
  }

  private isBlank(v: any): boolean {
    return v == null || v === '' || (typeof v === 'number' && !Number.isFinite(v));
  }
}
